// src/pages/agenda/GradeHoras.js — visões Semana e Dia (grade de horas).
//
// Fiel ao design: 52px por hora na semana, 60px no dia; compromissos que
// se sobrepõem dividem a coluna; linha verde da hora atual no dia de hoje.
// A grade vai das 07h às 20h e se estende sozinha quando há compromisso
// fora disso (decisão de out/26) — ninguém perde o que marcou às 06h.
//
// Clicar num espaço vazio da coluna abre "Novo compromisso" naquele dia e
// horário (arredondado pra meia hora).
//
// A hora tem 52px (semana) / 60px (dia) de MÍNIMO e estica pra preencher a
// área quando a tela é alta — com altura fixa sobrava uma faixa branca
// embaixo das 20h em monitor grande (e2e de out/26).
import { forwardRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { DC, diaLongo, dnum, dow, hh } from './datas';
import Faisca from '../../components/copiloto/Faisca';
import { FONT_NUM, LILAS, TIPOS, avatar, botaoBase, cor, corDoBloco, ehProposta, faixa, faixas, primeiroNome } from './estilo';

const H0_PADRAO = 7;
const H1_PADRAO = 20;

export function limitesDaGrade(eventos) {
  let h0 = H0_PADRAO; let h1 = H1_PADRAO;
  eventos.forEach((e) => { h0 = Math.min(h0, Math.floor(e.ini_h)); h1 = Math.max(h1, Math.ceil(e.fim_h)); });
  return { h0: Math.max(0, h0), h1: Math.min(24, h1) };
}

function sub(e) {
  if (e.tipo === 'consulta') return e.responsavel ? `com ${primeiroNome(e.responsavel.nome)}` : '';
  if (e.tipo === 'disparo') return `${e.disparo.contatos} contatos`;
  return e.responsavel ? primeiroNome(e.responsavel.nome) : '';
}
function linha2(e) {
  if (e.tipo === 'consulta') return [e.contato?.fone, e.status_txt].filter(Boolean).join(' · ');
  if (e.tipo === 'disparo') return [`Template ${e.disparo.template}`, e.disparo.pasta ? `pasta ${e.disparo.pasta}` : null, `${e.disparo.contatos} contatos`].filter(Boolean).join(' · ');
  return e.nota || e.status_txt;
}

function aoTeclar(fn) {
  return (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); fn(ev); } };
}

const GradeHoras = forwardRef(function GradeHoras({
  view, dias, porDia, hoje, agoraH, selId, onSel, onDia, onVazio, h0, h1, resumoDia, rolarPara, chaveRolagem,
}, gridRef) {
  const semana = view === 'semana';
  const [alturaUtil, setAlturaUtil] = useState(0);
  useEffect(() => {
    const el = gridRef?.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setAlturaUtil(el.clientHeight));
    ro.observe(el);
    setAlturaUtil(el.clientHeight);
    return () => ro.disconnect();
  }, [gridRef]);
  const rowH = Math.max(semana ? 52 : 60, Math.floor(alturaUtil / Math.max(1, h1 - h0)));

  // Rola pra um horário útil quando muda o que está na tela (não a cada
  // releitura): perto de agora se hoje aparece, senão o começo do dia.
  useEffect(() => {
    const el = gridRef?.current;
    if (!el || rolarPara == null) return;
    el.scrollTop = Math.max(0, (rolarPara - h0) * rowH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveRolagem]);
  const altura = (h1 - h0) * rowH;
  const horas = Array.from({ length: Math.max(0, h1 - h0 - 1) }, (_, i) => ({ label: hh(h0 + 1 + i), top: (i + 1) * rowH }));
  const colunas = semana ? `56px repeat(${dias.length}, minmax(0, 1fr))` : '56px minmax(0, 1fr)';
  const temHoje = dias.includes(hoje) && agoraH >= h0 && agoraH <= h1;
  const agoraTop = (agoraH - h0) * rowH;

  const clicarVazio = (dia) => (ev) => {
    if (!onVazio) return;
    const r = ev.currentTarget.getBoundingClientRect();
    const h = h0 + (ev.clientY - r.top) / rowH;
    onVazio(dia, Math.min(h1 - 0.5, Math.max(h0, Math.floor(h * 2) / 2)));
  };

  const bloco = (e, lane, n) => {
    const k = cor(TIPOS[e.tipo].c);
    const c = corDoBloco(e, k);
    const sel = selId === e.id;
    const top = (e.ini_h - h0) * rowH + 2;
    const h = Math.max((e.fim_h - e.ini_h) * rowH - 4, 20);
    const av = e.responsavel ? avatar(e.responsavel.id) : null;
    const comum = {
      position: 'absolute', top, height: h, left: `calc(${((lane * 100) / n).toFixed(3)}% + 3px)`, width: `calc(${(100 / n).toFixed(3)}% - 6px)`,
      boxSizing: 'border-box', bgcolor: c.bg, border: c.borda, boxShadow: sel ? `0 0 0 2px ${c.base}` : 'none',
      zIndex: sel ? 2 : 1, cursor: 'pointer', overflow: 'hidden',
      '&:focus-visible': { outline: '2px solid #9184d9', outlineOffset: 1 },
    };
    const abrir = (ev) => { ev.stopPropagation(); onSel(e.id); };
    const rotulo = `${ehProposta(e) ? 'Proposta do Copiloto: ' : ''}${faixa(e)} ${e.titulo}`;
    const selo = ehProposta(e) && h >= 40 && (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: LILAS, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
        <Faisca size={10} color={LILAS} />
        {/* Na semana a coluna é estreita: o rótulo curto cabe inteiro. */}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {e.proposta.operacao === 'remarcar'
            ? (semana ? 'Remarcação' : 'Remarcação proposta')
            : (semana ? 'Proposta' : 'Proposto pelo Copiloto')}
        </Box>
      </Box>
    );
    if (semana) {
      return (
        <Box key={e.id} role="button" tabIndex={0} aria-label={rotulo} title={e.titulo} onClick={abrir} onKeyDown={aoTeclar(abrir)}
          sx={{ ...comum, p: '3px 7px', borderRadius: '7px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {selo}
          {h < 40 ? (
            <Box component="span" sx={{ fontSize: '0.68rem', fontWeight: 700, color: c.texto, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 }}>
              <Box component="span" sx={{ fontFamily: FONT_NUM, fontWeight: 500, color: 'var(--ch-text2)', mr: '4px' }}>{e.ini}</Box>{e.titulo}
            </Box>
          ) : (
            <>
              <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: c.texto, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>{e.titulo}</Box>
              <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.65rem', color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{faixa(e)}</Box>
            </>
          )}
          {h >= 66 && (
            <Box component="span" sx={{ fontSize: '0.65rem', color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub(e)}</Box>
          )}
        </Box>
      );
    }
    return (
      <Box key={e.id} role="button" tabIndex={0} aria-label={rotulo} onClick={abrir} onKeyDown={aoTeclar(abrir)}
        sx={{ ...comum, p: '6px 12px', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {selo}
          <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 700, color: c.texto, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontWeight: 500, color: 'var(--ch-text2)', mr: '6px' }}>{faixa(e)}</Box>{e.titulo}
          </Box>
          {h >= 40 && (
            <Box component="span" sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{linha2(e)}</Box>
          )}
        </Box>
        {e.responsavel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <Box component="span" sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: av.bg, color: av.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 700 }}>
              {e.responsavel.iniciais}
            </Box>
            <Box component="span" sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)', display: { xs: 'none', sm: 'inline' } }}>{e.responsavel.nome}</Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: colunas, borderBottom: '1px solid var(--ch-bdr)', flex: 'none' }}>
        <Box />
        {semana ? dias.map((s) => {
          const ehHoje = s === hoje;
          return (
            <Box key={s} component="button" type="button" onClick={() => onDia(s)} aria-label={`Abrir ${diaLongo(s)}`}
              sx={{ ...botaoBase, border: 'none', borderLeft: '1px solid var(--ch-bdr)', bgcolor: ehHoje ? 'var(--ch-today)' : 'transparent', p: '8px 0 9px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', color: ehHoje ? 'var(--ch-green-d)' : 'var(--ch-text3)' }}>{DC[dow(s)]}</Box>
              <Box component="span" sx={{ minWidth: 30, height: 30, boxSizing: 'border-box', px: '6px', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 600, color: ehHoje ? 'var(--ch-on-green)' : 'var(--ch-text)', bgcolor: ehHoje ? 'var(--ch-green)' : 'transparent' }}>{dnum(s)}</Box>
            </Box>
          );
        }) : (
          <Box sx={{ borderLeft: '1px solid var(--ch-bdr)', p: '8px 14px 9px', display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <Box component="span" sx={{ minWidth: 30, height: 30, boxSizing: 'border-box', px: '6px', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 600, color: dias[0] === hoje ? 'var(--ch-on-green)' : 'var(--ch-text)', bgcolor: dias[0] === hoje ? 'var(--ch-green)' : 'var(--ch-inset)' }}>{dnum(dias[0])}</Box>
            <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ch-text)', whiteSpace: 'nowrap' }}>{diaLongo(dias[0])}</Box>
            <Box component="span" sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{resumoDia}</Box>
          </Box>
        )}
      </Box>
      <Box ref={gridRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'var(--ch-bdr2) transparent' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: colunas, height: altura, position: 'relative' }}>
          <Box sx={{ position: 'relative' }}>
            {horas.map((h) => (
              <Box key={h.label} component="span" sx={{ position: 'absolute', top: h.top, right: 8, transform: 'translateY(-50%)', fontFamily: FONT_NUM, fontSize: '0.66rem', color: 'var(--ch-text3)' }}>{h.label}</Box>
            ))}
            {semana && temHoje && (
              <Box component="span" sx={{ position: 'absolute', top: agoraTop, right: 4, transform: 'translateY(-50%)', zIndex: 4, bgcolor: 'var(--ch-green)', color: 'var(--ch-on-green)', fontFamily: FONT_NUM, fontSize: '0.62rem', fontWeight: 600, p: '1px 5px', borderRadius: '4px' }}>
                {hh(agoraH)}
              </Box>
            )}
          </Box>
          {dias.map((s) => {
            const ehHoje = s === hoje;
            const lista = porDia[s] || [];
            return (
              <Box key={s} onClick={clicarVazio(s)}
                sx={{
                  position: 'relative', borderLeft: '1px solid var(--ch-bdr)', bgcolor: semana && ehHoje ? 'var(--ch-today)' : 'transparent',
                  backgroundImage: `repeating-linear-gradient(to bottom, var(--ch-bdr) 0, var(--ch-bdr) 1px, transparent 1px, transparent ${rowH}px)`,
                  cursor: onVazio ? 'copy' : 'default',
                }}>
                {faixas(lista).map(({ e, lane, n }) => bloco(e, lane, n))}
                {ehHoje && temHoje && (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, top: agoraTop, height: 2, mt: '-1px', bgcolor: 'var(--ch-green)', zIndex: 3, pointerEvents: 'none' }}>
                    <Box sx={{ position: 'absolute', left: -5, top: -4, width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--ch-green)' }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});

export default GradeHoras;
